/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Dynamic from '@base/Dynamic/Dynamic';
import useSubscribed from '@hooks/useSubscribed';
import SEO from '@seo/index';
import goalSchema from '@seo/goal/goal';
import { FREE_HOME_PAGE } from '@constants/segment';
import {
  fetchGoalInfo,
  fetchGoalFAQs,
  fetchGoalPageBlocksNew,
  fetchDefaultStoreTabWhiteListedGoalUid,
  fetchSocialProofing
} from '@api/fetchers/goal';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import useSelectedGoal, {
  useUpdateSelectedGoal
} from '@api/hooks/topology/useSelectedGoal';
// SWR Hooks
import { checkGoalSwitch, checkIsFollowedGoal } from '@cont/App/initialize';
import cookies, { eraseCookie, readCookie } from '@utils/cookies';
import {
  ICONS_GOAL_UID,
  UA_ICONS_SLUG
} from '@constants/unacademyIcons/icons.constants';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getCheckPartPaymentStatus, getIsLoggedIn } from '@api/selectors/auth';
import useGroupJoinInvite from '@cont/Groups/groups-hooks/useGroupJoinInvite';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useFollowedGoals from '@api/hooks/goal/useFollowedGoals';
import { useIsMobile, usePageSeoContent } from '@stores/AppCommonData';
import fetchPageMetaInfo from '@api/fetchers/pageMetaInfo';
import { UNSAT_GOAL_UID } from '@constants/index';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT } from '@utils/abHelper';
import NewPresubscribed from '@cont/NewPreSubscribed/NewPresubscribed';
import NewGoalLayout from '@layout/NewGoalLayout/NewGoalLayout';
import useTopology, {
  getKey as getTopologyKey
} from '@api/hooks/topology/useTopology';
import usePartPaymentStatus from '@api/hooks/payments/usePartPaymentStatus';
import useGoalPageRedirection from '@cont/NewPreSubscribed/hooks/useGoalPageRedirection';
import validateAndSetSessionId, {
  DEVICE_ID_COOKIE_NAME
} from '@utils/session/validateAndSetSessionId';
import { emptyObject } from '@constants/empty';
import { HOME_PAGE } from '@constants/appFlyerLinks';
import AppInstallBanner from '@cont/Homepage/AppInstallBanner';
import usePresubscribedBlocks from '@cont/NewPreSubscribed/hooks/usePresubscribedBlocks';
import { HomePageInfoContextProvider } from '@cont/NewPreSubscribed/context/goalLanding';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { fetchUserProfileInfo } from '@api/fetchers/store';

const GroupsJoinDialog = Dynamic(() =>
  import('@cont/Groups/WidgetCards/GroupsJoinDialog')
);

const PlusHomePage = ({
  goalUID,
  querySlug,
  // Pre-fetched data from the server
  fallbackData
}) => {
  const { blockData, mutate: revalidateSubscribedBlocks } =
    usePresubscribedBlocks(goalUID, {
      fallbackData: fallbackData?.pageBlocks || {},
      revalidateOnMount: true
    });
  return (
    <HomePageInfoContextProvider
      value={{ blockData, revalidateSubscribedBlocks }}
    >
      <GoalLanding
        goalUID={goalUID}
        querySlug={querySlug}
        fallbackData={fallbackData}
      />
    </HomePageInfoContextProvider>
  );
};
const GoalLanding = ({
  goalUID,
  querySlug,
  // Pre-fetched data from the server
  fallbackData
}) => {
  const { goalInfo } = useGoalInfo(goalUID, {
    fallbackData: fallbackData.goalInfo
  });
  const [showBanner, setShowBanner] = useState(false);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isSubscribed = useSubscribed(goalUID);
  const { accessControl } = useAccessControl(goalUID);
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);
  const [shouldJoinGroupViaInvite, setShouldJoinGroupViaInvite] =
    useState(false);
  const { groupJoinError } = useGroupJoinInvite(shouldJoinGroupViaInvite);
  const { storeTabWhiteListedGoalUid = emptyObject } = fallbackData;
  const { slug: goalSlug, title, uid, ogImage, name } = goalInfo || {};
  useGoalPageRedirection({
    goalUID,
    goalSlug,
    querySlug,
    storeTabWhiteListedGoalUid
  });
  const router = useRouter();

  const { selectedGoal } = useSelectedGoal();
  const selectedGoalCookie = readCookie('selectedGoalUid');
  const { followedGoals } = useFollowedGoals();
  const isMobile = useIsMobile();
  useEffect(() => {
    if (isLoggedIn && followedGoals?.length)
      checkGoalSwitch(followedGoals, goalUID, selectedGoalCookie);
    if (selectedGoalCookie && selectedGoal && selectedGoalCookie !== goalUID)
      checkIsFollowedGoal(followedGoals, goalUID);
  }, [isLoggedIn, selectedGoalCookie, selectedGoal, followedGoals]);

  const accessToken = readCookie('accessToken');

  const setSelectedGoal = useUpdateSelectedGoal();

  const { topology: selectedTopologyInfo } = useTopology(
    accessToken && selectedGoalCookie && uid !== selectedGoalCookie
      ? selectedGoalCookie
      : null
  );

  const handleScroll = () => {
    if (!showBanner) {
      setShowBanner(true);
    }
  };

  const { mutate: fetchPartPaymentStatus } = usePartPaymentStatus(goalUID);

  useEffect(() => {
    if (!accessToken && selectedGoalCookie && uid !== selectedGoalCookie) {
      // If the user is logged out and already has a pre-selected goal cookie that isn't the current goal they're viewing, fetch that goal's info and let it be the selected one, show the user a prompt to change their selected goal.
      if (selectedTopologyInfo) {
        setSelectedGoal(selectedGoalCookie);
      } else eraseCookie('selectedGoalUid');
    }
  }, []);

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  useEffect(() => {
    // Invites & Groups
    const { inviteeUID } = router.query;
    if (inviteeUID) {
      if (isLoggedIn && !isSubscribed) setShouldJoinGroupViaInvite(true);
      else if (!accessToken)
        // User doesn't have an active session
        openLoginDrawer();
    }
  }, [isLoggedIn, isSubscribed, goalUID, router.query, accessToken]);

  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus) fetchPartPaymentStatus();
  }, [isLoggedIn, goalUID, checkPartPaymentStatus]);

  // free trial experiment initialisation
  useEffect(() => {
    validateAndSetSessionId();
    const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
    fetchABExperimentData(
      'seven_day_free_trial_web_experiment',
      abProxyJWT,
      deviceID
    );
  }, []);

  useEffect(() => {
    if (isMobile) {
      document.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (isMobile) {
        document.removeEventListener('scroll', handleScroll);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const { title: pageSEOTitle, description: pageSEODescription } =
    usePageSeoContent();
  const goalTitle = `${pageSEOTitle || title || name || ''} | Unacademy`;
  const goalDescription =
    pageSEODescription ||
    `Prepare live from Top Educators with comprehensive, structured and crash courses along with practice section for ${
      pageSEOTitle || title || name || ''
    } on Unacademy Plus.`;
  const url = `https://unacademy.com/goal/${goalSlug}/${uid}`;
  const imageURL =
    ogImage ||
    'https://static.uacdn.net/static/dist/c76c2e18c7980818a913f9467c74c567.png';

  return (
    <>
      <SEO
        title={goalTitle}
        url={url}
        description={goalDescription}
        openGraph={{
          title: goalTitle,
          description: goalDescription,
          images: [
            {
              url: imageURL
            }
          ],
          url
        }}
        schema={goalSchema(
          fallbackData?.faqs,
          title,
          url,
          fallbackData?.pageBlocks?.results || [],
          goalDescription
        )}
      />
      <NewPresubscribed
        data={fallbackData?.pageBlocks || {}}
        goalDetails={goalInfo}
        socialProof={fallbackData?.socialProof?.data}
      />
      {!!router.query.inviteeUID && !!groupJoinError && (
        <GroupsJoinDialog
          title={groupJoinError?.title}
          description={groupJoinError?.description}
          type="error"
          open={isLoggedIn && !!router.query.inviteeUID}
        />
      )}
      {showBanner && (
        <AppInstallBanner
          pageType="homePageDownloadBanner"
          downloadLink={HOME_PAGE}
        />
      )}
    </>
  );
};

PlusHomePage.pageInfo = {
  isMWebSupported: true,
  name: FREE_HOME_PAGE,
  layoutOptions: {
    hideLearnerMinutes: true,
    disableDarkMode: true
  }
};

export const getStaticProps = async ({ params, preview }) => {
  // Preview mode with getStaticProps ref: https://nextjs.org/docs/advanced-features/preview-mode#fetch-preview-data

  const { goalUID: routeGoalUID, goalSlug: querySlug } = params || {};
  if (!routeGoalUID || !querySlug) return { notFound: true };
  const goalUID = routeGoalUID.toUpperCase();
  try {
    if (goalUID === UNSAT_GOAL_UID)
      return {
        redirect: {
          destination: '/scholarship/UNSAT',
          permanent: true
        }
      };
    if (goalUID === ICONS_GOAL_UID)
      return {
        redirect: {
          destination: `/goal/${UA_ICONS_SLUG}/${ICONS_GOAL_UID}`,
          permanent: false
        }
      };

    const goal = await fetchGoalInfo(goalUID);
    const userProfile = await fetchUserProfileInfo(goalUID);
    if (!goal || goal.errorCode)
      return {
        redirect: {
          destination: `/goal/${querySlug}/${goalUID}/subscriptions`,
          permanent: false
        },
        revalidate: 5
      }; // Redirect user to subscriptions page.

    const showOldHomePage = goal.isPlatformGoal || goal.isRecordedGoal;

    if (showOldHomePage)
      return {
        redirect: { destination: `/goal/${querySlug}/${goalUID}/home` }
      };

    const goalSlug = goal.slug || querySlug;
    if (!goal.isPlusAvailable && !goal.isPlatformGoal)
      return {
        redirect: { destination: `/goal/${goalSlug}/${goalUID}/free-platform` }
      };

    const promises = [];
    promises.push(
      resolvePromiseToObject(
        fetchGoalPageBlocksNew({ goalUID, limit: 3 }),
        'pageBlocks'
      )
    );

    promises.push(resolvePromiseToObject(fetchGoalFAQs(), 'faqs'));

    promises.push(
      resolvePromiseToObject(
        fetchPageMetaInfo(PlusHomePage.pageInfo.name, goalUID, preview),
        'metaInfo'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchDefaultStoreTabWhiteListedGoalUid(),
        'storeTabWhiteListedGoalUid'
      )
    );

    promises.push(resolvePromiseToObject(fetchSocialProofing(), 'socialProof'));

    let fallbackData = {};
    if (promises.length > 0)
      fallbackData = await Promise.all(promises).then((resolvedVals) => {
        const resultObj = {};
        for (let i = 0; i < resolvedVals.length; i += 1)
          resultObj[resolvedVals[i].type] = resolvedVals[i].result;
        return resultObj;
      });

    fallbackData.goalInfo = goal;

    return {
      props: {
        goalUID,
        querySlug,
        pageMetaInfo: {
          ...fallbackData.metaInfo,
          showStoreTab: userProfile?.showStoreTab
        },
        fallbackData,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal
        },
        inPreviewMode: preview || false // Opt in to preview mode UI
      },
      revalidate: 5 // 5 seconds
    };
  } catch {
    return {
      redirect: {
        destination: `/goal/${querySlug}/${goalUID}/subscriptions`,
        permanent: false
      },
      revalidate: 5
    };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default PlusHomePage;

PlusHomePage.propTypes = {
  goalUID: PropTypes.string.isRequired
};
PlusHomePage.Layout = NewGoalLayout;
